import { Checkbox, Flex, Input, InputNumber, TimePicker } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { SlaCurrencySelect } from '~/app/landing/sla/shared/components/SlaCurrencySelect';
import { SlaFormField } from '~/app/landing/sla/shared/components/SlaFormField';
import { SlaModalFieldGrid } from '~/app/landing/sla/shared/components/SlaModalFieldGrid';
import { useMaintenanceSlaIssueHelp } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/hooks/useMaintenanceSlaIssueHelp';
import { useMaintenanceWeekdayOptions } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/hooks/useMaintenanceWeekdayOptions';
import type { MaintenanceSlaHeaderFieldsProps } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaHeaderFields/MaintenanceSlaHeaderFields';

export function MaintenanceSlaHeaderScheduleFields({
  draft,
  issues,
  onChange,
}: MaintenanceSlaHeaderFieldsProps) {
  const { t } = useTranslation('sla');
  const help = useMaintenanceSlaIssueHelp(issues);
  const weekdays = useMaintenanceWeekdayOptions();
  const minutesHelp = help('restoredMinutes');

  return (
    <SlaModalFieldGrid columns={3}>
      <SlaFormField label={t('oems.maintenance.fields.businessHoursStart')}>
        <TimePicker
          format="HH:mm"
          style={{ width: '100%' }}
          value={
            draft.businessHoursStart
              ? dayjs(draft.businessHoursStart, 'HH:mm')
              : null
          }
          onChange={(value) =>
            onChange({
              businessHoursStart: value ? value.format('HH:mm') : null,
            })
          }
        />
      </SlaFormField>
      <SlaFormField label={t('oems.maintenance.fields.businessHoursEnd')}>
        <TimePicker
          format="HH:mm"
          style={{ width: '100%' }}
          value={
            draft.businessHoursEnd
              ? dayjs(draft.businessHoursEnd, 'HH:mm')
              : null
          }
          onChange={(value) =>
            onChange({
              businessHoursEnd: value ? value.format('HH:mm') : null,
            })
          }
        />
      </SlaFormField>
      <SlaFormField label={t('oems.fields.currency')}>
        <SlaCurrencySelect
          style={{ width: '100%' }}
          value={draft.priceCurrency}
          onChange={(priceCurrency) =>
            onChange({ priceCurrency: String(priceCurrency) })
          }
        />
      </SlaFormField>
      <SlaFormField
        label={t('oems.maintenance.fields.workingDays')}
        style={{ gridColumn: '1 / -1' }}
      >
        <Checkbox.Group
          options={weekdays}
          value={draft.workingDays}
          onChange={(values) =>
            onChange({ workingDays: values.map((value) => Number(value)) })
          }
        />
      </SlaFormField>
      <SlaFormField
        label={t('oems.maintenance.fields.excludeHolidays')}
        style={{ gridColumn: '1 / -1' }}
      >
        <Checkbox
          checked={draft.excludeHolidays}
          onChange={(event) =>
            onChange({ excludeHolidays: event.target.checked })
          }
        >
          {t('oems.maintenance.fields.excludeHolidaysHint')}
        </Checkbox>
      </SlaFormField>
      <SlaFormField
        label={t('oems.maintenance.fields.exclusions')}
        style={{ gridColumn: '1 / -1' }}
      >
        <Input.TextArea
          value={draft.exclusionsText}
          autoSize={{ minRows: 2, maxRows: 4 }}
          onChange={(event) => onChange({ exclusionsText: event.target.value })}
        />
      </SlaFormField>
      <SlaFormField
        label={t('oems.maintenance.fields.recurrenceWindowDays')}
        extra={
          <span style={{ color: '#94a3b8', fontSize: 12 }}>
            {t('oems.maintenance.fields.recurrenceWindowDaysHint')}
          </span>
        }
      >
        <InputNumber
          min={0}
          precision={0}
          style={{ width: '100%' }}
          value={draft.recurrenceWindowDays}
          onChange={(recurrenceWindowDays) =>
            onChange({ recurrenceWindowDays })
          }
        />
      </SlaFormField>
      <SlaFormField
        label={t('oems.maintenance.fields.restoredAvailable')}
        validateStatus={minutesHelp ? 'error' : undefined}
        help={minutesHelp}
        extra={
          <span style={{ color: '#94a3b8', fontSize: 12 }}>
            {t('oems.maintenance.fields.restoredAvailableHint')}
          </span>
        }
        style={{ gridColumn: 'span 2' }}
      >
        <Flex vertical gap={6}>
          <Flex align="center" gap={6}>
            <InputNumber
              min={0}
              precision={0}
              style={{ width: 80 }}
              placeholder="0"
              value={draft.restoredAvailableHours}
              onChange={(restoredAvailableHours) =>
                onChange({ restoredAvailableHours })
              }
            />
            <span style={{ color: '#94a3b8', fontSize: 12 }}>
              {t('oems.maintenance.hrs')}
            </span>
          </Flex>
          <Flex align="center" gap={6}>
            <InputNumber
              min={0}
              max={59}
              precision={0}
              style={{ width: 80 }}
              placeholder="0"
              value={draft.restoredAvailableMinutes}
              onChange={(restoredAvailableMinutes) =>
                onChange({ restoredAvailableMinutes })
              }
            />
            <span style={{ color: '#94a3b8', fontSize: 12 }}>
              {t('oems.maintenance.min')}
            </span>
          </Flex>
        </Flex>
      </SlaFormField>
    </SlaModalFieldGrid>
  );
}
